import { query } from "@angular/animations";
import { Injectable } from "@angular/core";
import API, { graphqlOperation, GraphQLResult } from "@aws-amplify/api-graphql";
import { CancelFolderScanTaskMutation, ModelCancelFolderScanTaskInput } from "src/app/API.service";

export type CreateFolderInput = {
  id?: string | null;
  name: string;
  clientID?: string | null;
  downloadLink?: string | null;
  parentFolderID?: string | null;
};

export enum ScanStatus {
  INIT = "INIT",
  FAILED = "FAILED",
  SUCCESS = "SUCCESS"
}

export type GetFolderQuery = {
  __typename: "Folder";
  id: string;
  name: string;
  clientID?: string | null;
  downloadLink?: string | null;
  createdAt: string;
  updatedAt: string;
  invoices?: {
    __typename: "ModelInvoiceConnection";
    items: Array<{
      __typename: "Invoice";
      id: string;
      name: string;
      folderID: string;
      createdAt: string;
      updatedAt: string;
      scans?: {
        __typename: "ModelScanConnection";
        items: Array<{
          __typename: "Scan";
          id: string;
          invoiceID?: string | null;
          status?: ScanStatus | null;
          downloadLink?: string | null;
          createdAt: string;
          updatedAt: string;
        } | null>;
        nextToken?: string | null;
      } | null;
    } | null>;
    nextToken?: string | null;
  } | null;
};

export type UpdateFolderInput = {
  id: string;
  name?: string | null;
  clientID?: string | null;
  downloadLink?: string | null;
  parentFolderID?: string | null;
  isPasswordProtected?: boolean | null;
  passwordHash?: string | null;
};

export type UpdateFolderMutation = {
  __typename: "Folder";
  id: string;
  name: string;
  clientID?: string | null;
  downloadLink?: string | null;
  createdAt: string;
  updatedAt: string;
  invoices?: {
    __typename: "ModelInvoiceConnection";
    items: Array<{
      __typename: "Invoice";
      id: string;
      name: string;
      folderID: string;
      createdAt: string;
      updatedAt: string;
      scans?: {
        __typename: "ModelScanConnection";
        items: Array<{
          __typename: "Scan";
          id: string;
          invoiceID?: string | null;
          status?: ScanStatus | null;
          downloadLink?: string | null;
          createdAt: string;
          updatedAt: string;
        } | null>;
        nextToken?: string | null;
      } | null;
    } | null>;
    nextToken?: string | null;
  } | null;
};

export type CreateFolderMutation = {
  __typename: "Folder";
  id: string;
  name: string;
  clientID?: string | null;
  downloadLink?: string | null;
  createdAt: string;
  updatedAt: string;
  invoices?: {
    __typename: "ModelInvoiceConnection";
    items: Array<{
      __typename: "Invoice";
      id: string;
      name: string;
      folderID: string;
      createdAt: string;
      updatedAt: string;
      scans?: {
        __typename: "ModelScanConnection";
        items: Array<{
          __typename: "Scan";
          id: string;
          invoiceID?: string | null;
          status?: ScanStatus | null;
          downloadLink?: string | null;
          createdAt: string;
          updatedAt: string;
        } | null>;
        nextToken?: string | null;
      } | null;
    } | null>;
    nextToken?: string | null;
  } | null;
};

export type ModelFolderConditionInput = {
  name?: ModelStringInput | null;
  clientID?: ModelIDInput | null;
  downloadLink?: ModelStringInput | null;
  and?: Array<ModelFolderConditionInput | null> | null;
  or?: Array<ModelFolderConditionInput | null> | null;
  not?: ModelFolderConditionInput | null;
};

export enum ModelAttributeTypes {
  binary = "binary",
  binarySet = "binarySet",
  bool = "bool",
  list = "list",
  map = "map",
  number = "number",
  numberSet = "numberSet",
  string = "string",
  stringSet = "stringSet",
  _null = "_null"
}

export type ModelIDInput = {
  ne?: string | null;
  eq?: string | null;
  le?: string | null;
  lt?: string | null;
  ge?: string | null;
  gt?: string | null;
  contains?: string | null;
  notContains?: string | null;
  between?: Array<string | null> | null;
  beginsWith?: string | null;
  attributeExists?: boolean | null;
  attributeType?: ModelAttributeTypes | null;
  size?: ModelSizeInput | null;
};

export type ModelStringInput = {
  ne?: string | null;
  eq?: string | null;
  le?: string | null;
  lt?: string | null;
  ge?: string | null;
  gt?: string | null;
  contains?: string | null;
  notContains?: string | null;
  between?: Array<string | null> | null;
  beginsWith?: string | null;
  attributeExists?: boolean | null;
  attributeType?: ModelAttributeTypes | null;
  size?: ModelSizeInput | null;
};

export type ModelSizeInput = {
  ne?: number | null;
  eq?: number | null;
  le?: number | null;
  lt?: number | null;
  ge?: number | null;
  gt?: number | null;
  between?: Array<number | null> | null;
};

@Injectable({
  providedIn: 'root'
})
export class FolderService {
  async CreateFolder(
    input: CreateFolderInput,
    condition?: ModelFolderConditionInput
  ): Promise<CreateFolderMutation> {
    const statement = `mutation CreateFolder($input: CreateFolderInput!, $condition: ModelFolderConditionInput) {
        createFolder(input: $input, condition: $condition) {
          __typename
          id
          name
          clientID
          downloadLink
          status
          areThereMissingFields
          isLatestScanIncludedProducts
          parentFolderID
          isLatestScanIncludedProducts
          invoices {
            __typename
            nextToken
          }
          createdAt
          updatedAt
        }
      }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    if (condition) {
      gqlAPIServiceArguments.condition = condition;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <CreateFolderMutation>response.data.createFolder;
  }

  async CancelFolderScanTask(
    input?: ModelCancelFolderScanTaskInput
  ): Promise<CancelFolderScanTaskMutation> {
    const statement = `mutation CancelFolderScanTask($input: ModelCancelFolderScanTaskInput) {
        cancelFolderScanTask(input: $input) {
          __typename
          result
        }
      }`;
    const gqlAPIServiceArguments: any = {};
    if (input) {
      gqlAPIServiceArguments.input = input;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <CancelFolderScanTaskMutation>response.data.cancelFolderScanTask;
  }

  async UpdateFolder(
    input: UpdateFolderInput,
    condition?: ModelFolderConditionInput
  ): Promise<UpdateFolderMutation> {
    const statement = `mutation UpdateFolder($input: UpdateFolderInput!, $condition: ModelFolderConditionInput) {
        updateFolder(input: $input, condition: $condition) {
          __typename
          id
          name
          clientID
          downloadLink
          status
          latestFolderInvoiceTaskID
          isLatestScanIncludedProducts
          areThereMissingFields
          invoices {
            items {
              updatedAt
              name
              missingFields
              id
              folderID
              extractedProductsResults
              extractedResults
              createdAt
              thumbnailPath
            }
          }
          latestFolderInvoiceTask {
            __typename
            id
            folderID
            clientID
            taskCount
            processedTaskCount
            executionTime
            logMessages
            status
            extractedResults
            missingFieldsCount
            createdAt
            updatedAt
          }
          latestCompletedAt
          isPasswordProtected
          passwordHash
          createdAt
          updatedAt
        }
      }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    if (condition) {
      gqlAPIServiceArguments.condition = condition;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <UpdateFolderMutation>response.data.updateFolder;
  }

  async GetFolder(id: string): Promise<GetFolderQuery> {
    const statement = `query GetFolder($id: ID!) {
        getFolder(id: $id) {
          __typename
          id
          name
          clientID
          downloadLink
          createdAt
          updatedAt
          invoices {
            __typename
            items {
              __typename
              id
              name
              folderID
              createdAt
              updatedAt
              invoiceUserAssignments {
                items {
                  id
                  email
                }
              }
              nanonets {
                expiresAt
                fileID
                modelID
                redirectUrl
                previewUrl
              }

              scans {
                __typename
                items {
                  __typename
                  id
                  invoiceID
                  status
                  downloadLink
                  createdAt
                  updatedAt
                }
                nextToken
              }
            }
            nextToken
          }
        }
      }`;
    const gqlAPIServiceArguments: any = {
      id
    };
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <GetFolderQuery>response.data.getFolder;
  }

  async predictFolder(id: string, redirectUrl?: string): Promise<any> {
    // nanonets run folder
    const statement = `
mutation MyMutation($input: PredictFolderInput !) {
  predictFolder(input: $input) {
    invoices {
      id
      clientID
      createdAt
      extractedProductsResults
      extractedResults
      folderID
      index
      isLatestScanIncludedProducts
      missingFields
      name
      rotateAngle
      thumbnailPath
      updatedAt
      nanonetsID
      nanonets {
        expiresAt
        fileID
        modelID
        redirectUrl
        previewUrl
      }
    }
  }
}

        `;
    const variables: any = {
      input: {
        folderID: id,
        redirectUrl: redirectUrl
      }
    };
    const response = (await API.graphql(
      graphqlOperation(
        statement,
        variables,
        // "AMAZON_COGNITO_USER_POOLS"
      )
    )) as any;
    return response;
  }

  
  async exportFolderFromNano(id: string): Promise<any> {
    // nanonets run folder
    const statement = `
query MyQuery($folderID: ID !) {
  exportFolderFromNano(input: {folderID: $folderID}) {
    xlsxFileUrl
    csvFileUrl
  }
}
        `;
    const variables: any = {
        folderID: id
    };
    const response = (await API.graphql(
      graphqlOperation(
        statement,
        variables,
        // "AMAZON_COGNITO_USER_POOLS"
      )
    )) as any;
    return response;
  }

  async predictFolderAsync(id: string, redirectUrl?: string): Promise<any> {
    // nanonets run folder
    const statement = `
mutation MyMutation($input: PredictFolderInput !) {
  predictFolderAsync(input: $input) {
    statusCode
  }
}

        `;
    const variables: any = {
      input: {
        folderID: id,
        redirectUrl: redirectUrl
      }
    };
    const response = (await API.graphql(
      graphqlOperation(
        statement,
        variables,
        // "AMAZON_COGNITO_USER_POOLS"
      )
    )) as any;
    return response;
  }

  

}
